import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Heading,
  HStack,
  Input,
  NativeSelect,
  Spinner,
  Switch,
  Table,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { PencilIcon, PlusIcon, RefreshCwIcon, ShieldAlertIcon, XIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { PageHeader } from '~/components/shell'
import { useAuth } from '~/contexts/AuthContext'
import { type RoleName, type User, usersApi } from '~/lib/api'
import { toaster } from '~/toaster'

export const Route = createFileRoute('/_app/admin/users')({
  component: UsersPage,
})

const ROLES: { value: RoleName; label: string; color: string }[] = [
  { value: 'user', label: 'User', color: 'gray' },
  { value: 'operator', label: 'Operator', color: 'blue' },
  { value: 'admin', label: 'Admin', color: 'red' },
]

function CreateUserModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const [email, setEmail] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [role, setRole] = useState<RoleName>('user')

  const createMutation = useMutation({
    mutationFn: () =>
      usersApi.create({
        email,
        displayName: displayName || undefined,
        role,
      }),
    onSuccess: () => {
      toaster.create({
        title: 'User created',
        type: 'success',
      })
      onSuccess()
      onClose()
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to create user',
        description: error.message,
        type: 'error',
      })
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.includes('@')) {
      toaster.create({
        title: 'Invalid email',
        description: 'Please enter a valid email address',
        type: 'error',
      })
      return
    }
    createMutation.mutate()
  }

  return (
    <Box position="fixed" inset={0} bg="blackAlpha.600" zIndex={1000} onClick={onClose}>
      <Box
        position="fixed"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        bg="bg.panel"
        borderRadius="lg"
        w="90vw"
        maxW="500px"
        overflow="hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <Flex
          justify="space-between"
          align="center"
          p={4}
          borderBottomWidth="1px"
          borderColor="border"
        >
          <Heading size="md">Add New User</Heading>
          <Button size="sm" variant="ghost" onClick={onClose} aria-label="Close dialog">
            <XIcon size={16} />
          </Button>
        </Flex>

        <Box p={6}>
          <form onSubmit={handleSubmit}>
            <VStack gap={4} align="stretch">
              <Box>
                <Text fontWeight="medium" mb={1} fontSize="sm">
                  Email *
                </Text>
                <Input
                  type="email"
                  placeholder="user@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </Box>

              <Box>
                <Text fontWeight="medium" mb={1} fontSize="sm">
                  Display Name
                </Text>
                <Input
                  placeholder="John Doe"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                />
              </Box>

              <Box>
                <Text fontWeight="medium" mb={1} fontSize="sm">
                  Role
                </Text>
                <NativeSelect.Root>
                  <NativeSelect.Field
                    value={role}
                    onChange={(e) => setRole(e.target.value as RoleName)}
                  >
                    {ROLES.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </NativeSelect.Field>
                </NativeSelect.Root>
              </Box>

              <HStack gap={2} justify="flex-end" pt={4}>
                <Button variant="ghost" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit" colorPalette="blue" loading={createMutation.isPending}>
                  Create User
                </Button>
              </HStack>
            </VStack>
          </form>
        </Box>
      </Box>
    </Box>
  )
}

function EditUserModal({
  user,
  currentUserId,
  onClose,
  onSuccess,
}: {
  user: User
  currentUserId?: string
  onClose: () => void
  onSuccess: () => void
}) {
  const [email, setEmail] = useState(user.email)
  const [displayName, setDisplayName] = useState(user.displayName || '')
  const [role, setRole] = useState<RoleName>(user.role)
  const [isActive, setIsActive] = useState(user.isActive)

  const isCurrentUser = user.id === currentUserId

  const updateMutation = useMutation({
    mutationFn: () =>
      usersApi.update(user.id, {
        email: email !== user.email ? email : undefined,
        displayName:
          displayName !== (user.displayName || '') ? displayName || undefined : undefined,
        role: role !== user.role ? role : undefined,
        isActive: isActive !== user.isActive ? isActive : undefined,
      }),
    onSuccess: () => {
      toaster.create({
        title: 'User updated',
        type: 'success',
      })
      onSuccess()
      onClose()
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to update user',
        description: error.message,
        type: 'error',
      })
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.includes('@')) {
      toaster.create({
        title: 'Invalid email',
        description: 'Please enter a valid email address',
        type: 'error',
      })
      return
    }

    // Check if anything has changed
    const hasChanges =
      email !== user.email ||
      displayName !== (user.displayName || '') ||
      role !== user.role ||
      isActive !== user.isActive

    if (!hasChanges) {
      toaster.create({
        title: 'No changes',
        description: 'No changes were made',
        type: 'info',
      })
      onClose()
      return
    }

    updateMutation.mutate()
  }

  return (
    <Box position="fixed" inset={0} bg="blackAlpha.600" zIndex={1000} onClick={onClose}>
      <Box
        position="fixed"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        bg="bg.panel"
        borderRadius="lg"
        w="90vw"
        maxW="500px"
        overflow="hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <Flex
          justify="space-between"
          align="center"
          p={4}
          borderBottomWidth="1px"
          borderColor="border"
        >
          <Heading size="md">Edit User</Heading>
          <Button size="sm" variant="ghost" onClick={onClose} aria-label="Close dialog">
            <XIcon size={16} />
          </Button>
        </Flex>

        <Box p={6}>
          <form onSubmit={handleSubmit}>
            <VStack gap={4} align="stretch">
              <Box>
                <Text fontWeight="medium" mb={1} fontSize="sm">
                  Email *
                </Text>
                <Input
                  type="email"
                  placeholder="user@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </Box>

              <Box>
                <Text fontWeight="medium" mb={1} fontSize="sm">
                  Display Name
                </Text>
                <Input
                  placeholder="John Doe"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                />
              </Box>

              <Box>
                <Text fontWeight="medium" mb={1} fontSize="sm">
                  Role
                </Text>
                <NativeSelect.Root disabled={isCurrentUser}>
                  <NativeSelect.Field
                    value={role}
                    onChange={(e) => setRole(e.target.value as RoleName)}
                  >
                    {ROLES.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </NativeSelect.Field>
                </NativeSelect.Root>
                {isCurrentUser && (
                  <Text fontSize="xs" color="content.secondary" mt={1}>
                    You cannot change your own role
                  </Text>
                )}
              </Box>

              <Box>
                <Text fontWeight="medium" mb={1} fontSize="sm">
                  Status
                </Text>
                <HStack gap={2}>
                  <Switch.Root
                    checked={isActive}
                    onCheckedChange={(e) => setIsActive(e.checked)}
                    disabled={isCurrentUser}
                  >
                    <Switch.HiddenInput />
                    <Switch.Control />
                  </Switch.Root>
                  <Text fontSize="sm">{isActive ? 'Active' : 'Inactive'}</Text>
                </HStack>
                {isCurrentUser && (
                  <Text fontSize="xs" color="content.secondary" mt={1}>
                    You cannot deactivate yourself
                  </Text>
                )}
              </Box>

              <HStack gap={2} justify="flex-end" pt={4}>
                <Button variant="ghost" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit" colorPalette="blue" loading={updateMutation.isPending}>
                  Save Changes
                </Button>
              </HStack>
            </VStack>
          </form>
        </Box>
      </Box>
    </Box>
  )
}

function UsersPage() {
  const { user: currentUser, hasMinRole, isLoading: authLoading } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)

  const isAdmin = hasMinRole('admin')

  // Redirect non-admins after a short delay
  useEffect(() => {
    if (!authLoading && !isAdmin) {
      const timer = setTimeout(() => {
        navigate({ to: '/' })
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [authLoading, isAdmin, navigate])

  // Show access denied for non-admins
  if (!authLoading && !isAdmin) {
    return (
      <Flex h="100%" align="center" justify="center">
        <VStack gap={4} textAlign="center" p={8}>
          <Box p={4} borderRadius="full" bg="danger.subtle">
            <ShieldAlertIcon size={48} color="var(--chakra-colors-red-500)" />
          </Box>
          <Heading size="lg" color="danger.fg">
            Access Denied
          </Heading>
          <Text color="content.secondary" maxW="400px">
            This page is restricted to <strong>admin</strong> users only.
          </Text>
          <Text fontSize="sm" color="content.secondary">
            Redirecting to home page...
          </Text>
        </VStack>
      </Flex>
    )
  }

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list(),
    retry: false,
  })

  const updateRoleMutation = useMutation({
    mutationFn: ({ id, role }: { id: string; role: RoleName }) => usersApi.updateRole(id, role),
    onSuccess: () => {
      toaster.create({
        title: 'Role updated',
        type: 'success',
      })
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to update role',
        description: error.message,
        type: 'error',
      })
    },
  })

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      usersApi.updateStatus(id, isActive),
    onSuccess: () => {
      toaster.create({
        title: 'Status updated',
        type: 'success',
      })
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to update status',
        description: error.message,
        type: 'error',
      })
    },
  })

  const users = data?.users || []

  return (
    <Box maxW="1400px" mx="auto">
      <PageHeader
        title="User Management"
        subtitle="Manage user roles and account status"
        actions={
          <>
            <Button size="sm" colorPalette="blue" onClick={() => setShowCreateModal(true)}>
              <PlusIcon size={16} />
              Add User
            </Button>
            <Button size="sm" variant="outline" onClick={() => refetch()}>
              <RefreshCwIcon size={16} />
              Refresh
            </Button>
          </>
        }
      />

      <VStack align="stretch" gap={6}>
        <Card.Root>
          <Table.Root>
            <Table.Header>
              <Table.Row bg="bg.subtle">
                <Table.ColumnHeader>User</Table.ColumnHeader>
                <Table.ColumnHeader>Role</Table.ColumnHeader>
                <Table.ColumnHeader>Status</Table.ColumnHeader>
                <Table.ColumnHeader>Last Login</Table.ColumnHeader>
                <Table.ColumnHeader>Created</Table.ColumnHeader>
                <Table.ColumnHeader w="80px">Actions</Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {isLoading ? (
                <Table.Row>
                  <Table.Cell colSpan={6}>
                    <Flex justify="center" py={8}>
                      <Spinner />
                    </Flex>
                  </Table.Cell>
                </Table.Row>
              ) : users.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={6}>
                    <Text textAlign="center" py={8} color="content.secondary">
                      No users found
                    </Text>
                  </Table.Cell>
                </Table.Row>
              ) : (
                users.map((user) => (
                  <Table.Row key={user.id}>
                    <Table.Cell>
                      <VStack align="flex-start" gap={0}>
                        <Text fontWeight="medium">{user.displayName || user.email}</Text>
                        <Text fontSize="sm" color="content.secondary">
                          {user.email}
                        </Text>
                      </VStack>
                    </Table.Cell>
                    <Table.Cell>
                      <NativeSelect.Root size="sm" w="150px" disabled={user.id === currentUser?.id}>
                        <NativeSelect.Field
                          value={user.role}
                          onChange={(e) => {
                            if (user.id === currentUser?.id) {
                              toaster.create({
                                title: 'Cannot change your own role',
                                type: 'warning',
                              })
                              return
                            }
                            updateRoleMutation.mutate({
                              id: user.id,
                              role: e.target.value as RoleName,
                            })
                          }}
                        >
                          {ROLES.map((role) => (
                            <option key={role.value} value={role.value}>
                              {role.label}
                            </option>
                          ))}
                        </NativeSelect.Field>
                      </NativeSelect.Root>
                    </Table.Cell>
                    <Table.Cell>
                      <HStack gap={2}>
                        <Switch.Root
                          checked={user.isActive}
                          onCheckedChange={(e) => {
                            if (user.id === currentUser?.id) {
                              toaster.create({
                                title: 'Cannot deactivate yourself',
                                type: 'warning',
                              })
                              return
                            }
                            updateStatusMutation.mutate({
                              id: user.id,
                              isActive: e.checked,
                            })
                          }}
                          disabled={user.id === currentUser?.id}
                        >
                          <Switch.HiddenInput />
                          <Switch.Control />
                        </Switch.Root>
                        <Badge colorPalette={user.isActive ? 'green' : 'red'} variant="subtle">
                          {user.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </HStack>
                    </Table.Cell>
                    <Table.Cell>
                      <Text fontSize="sm">
                        {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : 'Never'}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text fontSize="sm">{new Date(user.createdAt).toLocaleDateString()}</Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Button size="sm" variant="ghost" onClick={() => setEditingUser(user)}>
                        <PencilIcon size={14} />
                        Edit
                      </Button>
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          </Table.Root>
        </Card.Root>
      </VStack>

      {showCreateModal && (
        <CreateUserModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => queryClient.invalidateQueries({ queryKey: ['users'] })}
        />
      )}

      {editingUser && (
        <EditUserModal
          user={editingUser}
          currentUserId={currentUser?.id}
          onClose={() => setEditingUser(null)}
          onSuccess={() => queryClient.invalidateQueries({ queryKey: ['users'] })}
        />
      )}
    </Box>
  )
}
